/**
 * PreviewLinkButton — reusable admin-only button to generate a short-lived
 * preview link for the Successor Claim portal or the Trusted Professional portal.
 *
 * Why this exists:
 *   Both flows are gated by one-time tokens normally issued only when an admin
 *   formally initiates a transfer / invites a trusted professional. For staff
 *   training, live demos to prospects, and post-deploy smoke testing, we need
 *   a way to walk the flow without spamming a real successor / professional.
 *   This component does exactly that — generates a 1-hour preview token via
 *   the given endpoint and surfaces the URL in a small dialog with copy /
 *   open-in-new-tab buttons.
 *
 * Anyone with the URL can open it; the admin-only gate is only on GENERATION.
 */
import React, { useState } from 'react';
import { Eye, Copy, ExternalLink, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PreviewLinkButton = ({ endpoint, label = 'Preview', testIdSuffix }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const generate = async () => {
    setLoading(true);
    try {
      const tok = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: { Authorization: `Bearer ${tok}` },
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not generate preview link.');
      const d = await r.json();
      const full = d.url?.startsWith('http') ? d.url : `${window.location.origin}${d.url}`;
      setUrl(full);
      setExpiresAt(d.expires_at || '');
      setOpen(true);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(url);
    toast.success('Preview link copied to clipboard.');
  };

  return (
    <>
      <button
        type="button"
        onClick={generate}
        disabled={loading}
        className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-600/20 hover:bg-amber-600/30 text-amber-200 border border-amber-600/40 rounded text-xs transition"
        data-testid={`preview-link-${testIdSuffix}`}
        title="Generate a 1-hour preview link — for staff training, demos, or self-testing. No emails sent."
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="preview-link-dialog">
          <DialogHeader>
            <DialogTitle>Preview link ready</DialogTitle>
            <DialogDescription className="text-sm">
              Anyone with this URL can walk through the flow. It auto-expires in 1 hour
              and no real emails are sent. Use it for self-testing, staff training,
              or live demos.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-xs font-mono break-all text-slate-700" data-testid="preview-link-url">
            {url}
          </div>
          {expiresAt && (
            <p className="text-xs text-slate-500 mt-1">
              Expires: {new Date(expiresAt).toLocaleString()}
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={copyUrl} data-testid="preview-link-copy">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy URL
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
              data-testid="preview-link-open"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in new tab
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PreviewLinkButton;
